export const MONOPOLY_COLORS = ["brown", "light_blue", "pink", "orange", "red", "yellow", "green", "dark_blue", "railroad", "utility"] as const;
export type MonopolyColor = typeof MONOPOLY_COLORS[number];
export type MonopolyCardKind = "money" | "property" | "wild" | "action" | "rent" | "improvement";
export type MonopolyAction = "pass_go" | "sly_deal" | "forced_deal" | "deal_breaker" | "debt_collector" | "birthday" | "just_say_no" | "double_rent" | "house" | "hotel" | "rent";

export type MonopolyCard = {
  id: string;
  key: string;
  name: string;
  kind: MonopolyCardKind;
  value: number;
  colors?: MonopolyColor[];
  action?: MonopolyAction;
};

export type MonopolyPlacedProperty = { cardId: string; color: MonopolyColor };
export type MonopolyImprovement = { cardId: string; color: MonopolyColor };

export const MONOPOLY_COLOR_RULES: Record<MonopolyColor, { label: string; size: number; rent: number[] }> = {
  brown: { label: "Brown", size: 2, rent: [1, 2] },
  light_blue: { label: "Light Blue", size: 3, rent: [1, 2, 3] },
  pink: { label: "Pink", size: 3, rent: [1, 2, 4] },
  orange: { label: "Orange", size: 3, rent: [1, 3, 5] },
  red: { label: "Red", size: 3, rent: [2, 3, 6] },
  yellow: { label: "Yellow", size: 3, rent: [2, 4, 6] },
  green: { label: "Green", size: 3, rent: [2, 4, 7] },
  dark_blue: { label: "Dark Blue", size: 2, rent: [3, 8] },
  railroad: { label: "Railroads", size: 4, rent: [1, 2, 3, 4] },
  utility: { label: "Utilities", size: 2, rent: [1, 2] },
};

type CardSeed = Omit<MonopolyCard, "id"> & { count: number };

const property = (key: string, name: string, value: number, color: MonopolyColor): CardSeed => ({ key, name, value, kind: "property", colors: [color], count: 1 });
const action = (key: string, name: string, value: number, actionName: MonopolyAction, count: number): CardSeed => ({ key, name, value, kind: actionName === "rent" ? "rent" : actionName === "house" || actionName === "hotel" ? "improvement" : "action", action: actionName, count });

const CARD_SEEDS: CardSeed[] = [
  property("mediterranean", "Mediterranean Avenue", 1, "brown"), property("baltic", "Baltic Avenue", 1, "brown"),
  property("oriental", "Oriental Avenue", 1, "light_blue"), property("vermont", "Vermont Avenue", 1, "light_blue"), property("connecticut", "Connecticut Avenue", 1, "light_blue"),
  property("st_charles", "St. Charles Place", 2, "pink"), property("states", "States Avenue", 2, "pink"), property("virginia", "Virginia Avenue", 2, "pink"),
  property("st_james", "St. James Place", 2, "orange"), property("tennessee", "Tennessee Avenue", 2, "orange"), property("new_york", "New York Avenue", 2, "orange"),
  property("kentucky", "Kentucky Avenue", 3, "red"), property("indiana", "Indiana Avenue", 3, "red"), property("illinois", "Illinois Avenue", 3, "red"),
  property("atlantic", "Atlantic Avenue", 3, "yellow"), property("ventnor", "Ventnor Avenue", 3, "yellow"), property("marvin", "Marvin Gardens", 3, "yellow"),
  property("pacific", "Pacific Avenue", 4, "green"), property("north_carolina", "North Carolina Avenue", 4, "green"), property("pennsylvania_ave", "Pennsylvania Avenue", 4, "green"),
  property("park_place", "Park Place", 4, "dark_blue"), property("boardwalk", "Boardwalk", 4, "dark_blue"),
  property("reading_rr", "Reading Railroad", 2, "railroad"), property("pennsylvania_rr", "Pennsylvania Railroad", 2, "railroad"), property("b_and_o", "B. & O. Railroad", 2, "railroad"), property("short_line", "Short Line", 2, "railroad"),
  property("electric", "Electric Company", 2, "utility"), property("water", "Water Works", 2, "utility"),

  { key: "wild_brown_light_blue", name: "Brown / Light Blue Wild", kind: "wild", value: 1, colors: ["brown", "light_blue"], count: 1 },
  { key: "wild_light_blue_railroad", name: "Light Blue / Railroad Wild", kind: "wild", value: 4, colors: ["light_blue", "railroad"], count: 1 },
  { key: "wild_pink_orange", name: "Pink / Orange Wild", kind: "wild", value: 2, colors: ["pink", "orange"], count: 2 },
  { key: "wild_red_yellow", name: "Red / Yellow Wild", kind: "wild", value: 3, colors: ["red", "yellow"], count: 2 },
  { key: "wild_dark_blue_green", name: "Dark Blue / Green Wild", kind: "wild", value: 4, colors: ["dark_blue", "green"], count: 1 },
  { key: "wild_green_railroad", name: "Green / Railroad Wild", kind: "wild", value: 4, colors: ["green", "railroad"], count: 1 },
  { key: "wild_railroad_utility", name: "Railroad / Utility Wild", kind: "wild", value: 2, colors: ["railroad", "utility"], count: 1 },
  { key: "wild_any", name: "Any Color Wild", kind: "wild", value: 0, colors: [...MONOPOLY_COLORS], count: 2 },

  action("deal_breaker", "Deal Breaker", 5, "deal_breaker", 2), action("forced_deal", "Forced Deal", 3, "forced_deal", 3),
  action("sly_deal", "Sly Deal", 3, "sly_deal", 3), action("just_say_no", "Just Say No!", 4, "just_say_no", 3),
  action("debt_collector", "Debt Collector", 3, "debt_collector", 3), action("birthday", "It’s My Birthday", 2, "birthday", 3),
  action("double_rent", "Double the Rent", 1, "double_rent", 2), action("house", "House", 3, "house", 3), action("hotel", "Hotel", 4, "hotel", 2),
  action("pass_go", "Pass Go", 1, "pass_go", 10),

  { ...action("rent_brown_light_blue", "Brown / Light Blue Rent", 1, "rent", 2), colors: ["brown", "light_blue"] },
  { ...action("rent_pink_orange", "Pink / Orange Rent", 1, "rent", 2), colors: ["pink", "orange"] },
  { ...action("rent_red_yellow", "Red / Yellow Rent", 1, "rent", 2), colors: ["red", "yellow"] },
  { ...action("rent_dark_blue_green", "Dark Blue / Green Rent", 1, "rent", 2), colors: ["dark_blue", "green"] },
  { ...action("rent_railroad_utility", "Railroad / Utility Rent", 1, "rent", 2), colors: ["railroad", "utility"] },
  { ...action("rent_any", "Wild Rent", 3, "rent", 3), colors: [...MONOPOLY_COLORS] },

  { key: "money_1", name: "$1M", kind: "money", value: 1, count: 6 }, { key: "money_2", name: "$2M", kind: "money", value: 2, count: 5 },
  { key: "money_3", name: "$3M", kind: "money", value: 3, count: 3 }, { key: "money_4", name: "$4M", kind: "money", value: 4, count: 3 },
  { key: "money_5", name: "$5M", kind: "money", value: 5, count: 2 }, { key: "money_10", name: "$10M", kind: "money", value: 10, count: 1 },
];

export function createMonopolyDealDeck(): MonopolyCard[] {
  return CARD_SEEDS.flatMap(({ count, ...seed }) => Array.from({ length: count }, (_, index) => ({ ...seed, id: `${seed.key}-${index + 1}` })));
}

export function shuffleMonopolyDealDeck(random = Math.random) {
  const deck = createMonopolyDealDeck();
  for (let index = deck.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [deck[index], deck[swap]] = [deck[swap], deck[index]];
  }
  return deck;
}

export function monopolyCardMap(cards: MonopolyCard[]) {
  return new Map(cards.map((card) => [card.id, card]));
}

export function monopolyIsComplete(properties: MonopolyPlacedProperty[], color: MonopolyColor) {
  return properties.filter((propertyCard) => propertyCard.color === color).length >= MONOPOLY_COLOR_RULES[color].size;
}

export function monopolyCompleteColors(properties: MonopolyPlacedProperty[]) {
  return MONOPOLY_COLORS.filter((color) => monopolyIsComplete(properties, color));
}

export function monopolyRent(properties: MonopolyPlacedProperty[], improvements: MonopolyImprovement[], color: MonopolyColor, cards: Map<string, MonopolyCard>) {
  const count = Math.min(properties.filter((propertyCard) => propertyCard.color === color).length, MONOPOLY_COLOR_RULES[color].size);
  if (!count) return 0;
  let rent = MONOPOLY_COLOR_RULES[color].rent[count - 1];
  if (count === MONOPOLY_COLOR_RULES[color].size && color !== "railroad" && color !== "utility") {
    const upgrades = improvements.filter((improvement) => improvement.color === color).map((improvement) => cards.get(improvement.cardId)?.action);
    if (upgrades.includes("house")) rent += 3;
    if (upgrades.includes("hotel")) rent += 4;
  }
  return rent;
}

export function monopolyAssetValue(cardIds: string[], cards: Map<string, MonopolyCard>) {
  return cardIds.reduce((total, cardId) => total + (cards.get(cardId)?.value || 0), 0);
}
